/**
 * Changelog ID: TCC-CONT-006
 * Author: Cascade AI
 * Date: 2025-05-28
 * Summary: Updated content loader to remove landing pages support
 * Following WindSurf Global Rules for structured, testable code
 *
 * Changelog ID: ACC-034
 * Author: Cascade AI
 * Date: 2025-07-07
 * Summary: Updated content loader to use dynamic imports for JSON files
 * Following WindSurf Global Rules for structured, testable code
 */

// Import utility for dynamic JSON imports
import { importAllJsonFromContext } from './fileUtils';

// Declare webpack's require.context for TypeScript
declare const require: {
  context: (directory: string, useSubdirectories: boolean, regExp: RegExp) => any;
};

// Types for our content
export interface BaseContent {
  title: string;
  slug: string;
  type: string;
  image: string;
}

export interface ProductContent extends BaseContent {
  type: 'product';
  description: string;
  allergens: string[];
  tags: string[];
  available: boolean;
  featured: boolean;
  notes?: string;
}

export type Content = ProductContent;

// Dynamic import of all product JSON files
// Using Webpack's require.context to import all files in the products directory
const productContext = require.context('../content/products/', false, /\.json$/);
const productModules = importAllJsonFromContext(productContext);

// Convert imported modules to array of ProductContent
const productData: ProductContent[] = Object.values(productModules).map(product => product as ProductContent);

/**
 * Loads content based on type and optional slug filter
 * Uses JSON files for products
 *
 * Changelog ID: TCC-CONT-006
 * Author: Cascade AI
 * Date: 2025-05-28
 */
export const loadContent = async (contentType: string, slug?: string): Promise<Content[]> => {
  // Get the right data based on content type
  let contents: Content[] = [];
  
  switch (contentType) {
    case 'products':
      contents = productData;
      break;
    default:
      console.warn(`Unknown content type: ${contentType}`);
  }
  
  // Filter by slug if provided
  if (slug) {
    contents = contents.filter(item => item.slug === slug);
  }
  
  // Log what we found for debugging
  console.log(`Found ${contents.length} ${contentType} items${slug ? ` with slug '${slug}'` : ''}`);
  
  return contents;
};
